<template>
  <div class="p-6">
    <h2 class="text-2xl font-semibold mb-6">My Text Notes</h2>

    <div v-if="textStore.notes.length > 0">
      <div class="mb-4">
        <FilterInput v-model="searchQuery" placeholder="Search notes..." />
      </div>

      <div v-if="bubblesStore.loading" class="text-center py-8">
        <p class="text-gray-600">Loading notes...</p>
      </div>
      <div v-else-if="bubblesStore.error" class="text-center py-8 text-red-500">
        <p>{{ bubblesStore.error }}</p>
      </div>
      <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <AppLink
          v-for="note in textStore.filteredNotes"
          :key="note.uuid"
          :to="`/bubble/${note.uuid}`"
        >
          <div class="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer">
            <h3 class="text-xl font-medium mb-4">{{ note.title }}</h3>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div
              class="text-gray-600 mb-2 prose prose-sm max-h-24 overflow-hidden"
              v-html="renderedContents[note.content] || ''"
            ></div>
            <div class="text-sm text-gray-500 mt-2">
              <span>Created: {{ formatDate(note.createdAt) }}</span>
              <span v-if="note.lastModified" class="ml-2"
                >Modified: {{ formatDate(note.lastModified) }}</span
              >
            </div>
          </div>
        </AppLink>
      </div>
    </div>
    <div v-else class="text-center py-8">
      <p class="text-gray-600 mb-4">You don't have any text notes yet.</p>
      <Button @click="navigateToAddNote">Add Your First Note</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useBubblesStore } from '@/stores/bubbles'
import { useTextStore } from '@/stores/core/textStore'
import FilterInput from '@/components/bubbles/view/helpers/FilterInput.vue'
import { useRouter } from 'vue-router'
import type { TextBubble } from '@/stores/interfaces'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { Button } from '@/components/ui/button'
import AppLink from '@/components/ui/link/AppLink.vue'

const router = useRouter()
const bubblesStore = useBubblesStore()
const textStore = useTextStore()

const searchQuery = computed({
  get: () => textStore.searchQuery,
  set: (value) => textStore.setSearchQuery(value),
})

const renderedContents = ref<Record<string, string>>({})

onMounted(async () => {
  textStore.setSearchQuery('')

  if (!bubblesStore.bubbleTypes['text']) {
    await bubblesStore.fetchBubbleTypes()
  }
  if (!bubblesStore.bubblesByType['text'] || bubblesStore.bubblesByType['text'].length < 5) {
    await bubblesStore.fetchBubblesByType('text')
  }
  textStore.notes = (bubblesStore.bubblesByType['text'] as TextBubble[]) || []
})

function navigateToAddNote() {
  router.push('/dashboard/add')
}

async function renderMarkdown(content: string) {
  if (!renderedContents.value[content]) {
    const rawMarkdown = await marked(content)
    renderedContents.value[content] = DOMPurify.sanitize(rawMarkdown)
  }
  return renderedContents.value[content] || ''
}

function formatDate(date: string | Date) {
  return new Date(date).toLocaleDateString()
}

watch(
  () => textStore.notes,
  async () => {
    for (const note of textStore.notes) {
      await renderMarkdown(note.content)
    }
  },
  { immediate: true }
)
</script>
